import * as THREE from 'three';
import { bakeSpallAtlas } from './atlas';
import { createChamferedBoxGeometry } from './brickGeometry';
import { createBrickMaterial, createDebrisMaterial, type BrickMaterialHandles } from './brickMaterial';
import { DEBRIS_CAP, type DebrisProp } from './debris';
import { hash01 } from './hash';
import type { DamageLedger } from './ledger';
import { weatheredTint } from './palette';
import {
  RESIDUE_MAP_H,
  RESIDUE_MAP_W,
  bakeResidueMap,
  emptyResidue,
  type ResidueStamp,
  type ResidueState,
} from './residue';
import type { ResolvedWallSpec } from './wallGrid';

const STATE_CODE: Record<string, number> = {
  intact: 0,
  spalled: 1,
  cracked: 2,
  rim: 3,
  removed: 4,
};

const DEBUG_COLORS: Record<string, [number, number, number]> = {
  intact: [0.55, 0.22, 0.16],
  spalled: [0.86, 0.48, 0.16],
  cracked: [0.92, 0.78, 0.22],
  rim: [0.78, 0.28, 0.82],
  removed: [0.06, 0.05, 0.05],
};

export interface WallView {
  readonly group: THREE.Group;
  readonly pickables: THREE.Object3D[];
  sync(ledger: DamageLedger, residue?: ResidueState, stamps?: readonly ResidueStamp[], debris?: readonly DebrisProp[]): void;
  setDebug(on: boolean): void;
  blitLedger(canvas: HTMLCanvasElement, ledger: DamageLedger): void;
  dispose(): void;
}

function makeAtlasTexture(): THREE.DataTexture {
  const baked = bakeSpallAtlas();
  const tex = new THREE.DataTexture(baked.data, baked.width, baked.height, THREE.RGBAFormat);
  tex.colorSpace = THREE.NoColorSpace;
  tex.magFilter = THREE.LinearFilter;
  tex.minFilter = THREE.LinearMipmapLinearFilter;
  tex.generateMipmaps = true;
  tex.wrapS = THREE.ClampToEdgeWrapping;
  tex.wrapT = THREE.ClampToEdgeWrapping;
  tex.anisotropy = 1;
  tex.needsUpdate = true;
  tex.flipY = false;
  return tex;
}

export function createWallView(spec: ResolvedWallSpec, ledger: DamageLedger): WallView {
  const group = new THREE.Group();
  group.name = 'damage-wall';

  const atlas = makeAtlasTexture();
  const handles: BrickMaterialHandles = createBrickMaterial(atlas, {
    x: spec.brickLength * 0.5,
    y: spec.brickHeight * 0.5,
    z: spec.brickDepth * 0.5,
  });

  const count = ledger.bricks.length;
  const geom = createChamferedBoxGeometry(spec.brickLength, spec.brickHeight, spec.brickDepth);
  const instA = new THREE.InstancedBufferAttribute(new Float32Array(count * 4), 4);
  const instB = new THREE.InstancedBufferAttribute(new Float32Array(count * 4), 4);
  const instC = new THREE.InstancedBufferAttribute(new Float32Array(count * 4), 4);
  const instD = new THREE.InstancedBufferAttribute(new Float32Array(count * 4), 4);
  const instE = new THREE.InstancedBufferAttribute(new Float32Array(count * 4), 4);
  const instF = new THREE.InstancedBufferAttribute(new Float32Array(count * 4), 4);
  geom.setAttribute('instanceA', instA);
  geom.setAttribute('instanceB', instB);
  geom.setAttribute('instanceC', instC);
  geom.setAttribute('instanceD', instD);
  geom.setAttribute('instanceE', instE);
  geom.setAttribute('instanceF', instF);

  const mesh = new THREE.InstancedMesh(geom, handles.material, count);
  mesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
  mesh.castShadow = false;
  mesh.receiveShadow = false;
  mesh.frustumCulled = false;
  mesh.name = 'damage-bricks';

  const dummy = new THREE.Object3D();
  const color = new THREE.Color();
  const tints: Array<[number, number, number]> = [];
  const rotX: number[] = [];
  const rotY: number[] = [];
  const rotZ: number[] = [];
  const jitterX: number[] = [];
  const jitterY: number[] = [];

  for (let i = 0; i < count; i += 1) {
    const seed = ledger.bricks[i].seed;
    tints.push(weatheredTint(hash01(seed, 3), hash01(seed, 8), hash01(seed, 13)));
    rotX.push((hash01(seed, 21) - 0.5) * 0.028);
    rotY.push((hash01(seed, 22) - 0.5) * 0.028);
    rotZ.push((hash01(seed, 23) - 0.5) * 0.024);
    jitterX.push((hash01(seed, 24) - 0.5) * 0.003);
    jitterY.push((hash01(seed, 25) - 0.5) * 0.002);
    color.setRGB(tints[i][0], tints[i][1], tints[i][2]);
    mesh.setColorAt(i, color);
  }
  if (mesh.instanceColor) mesh.instanceColor.needsUpdate = true;

  const jointCount = ledger.joints.length;
  const jointGeom = new THREE.BoxGeometry(1, 1, 1);
  const jointMat = new THREE.MeshStandardMaterial({
    color: 0x1a120e,
    roughness: 1,
    metalness: 0,
    dithering: false,
  });
  const jointMesh = new THREE.InstancedMesh(jointGeom, jointMat, Math.max(1, jointCount));
  jointMesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
  jointMesh.frustumCulled = false;
  jointMesh.name = 'damage-cracked-joints';
  jointMesh.castShadow = false;
  jointMesh.receiveShadow = false;

  const padX = spec.pitchX * 0.55;
  const padY = spec.joint;
  const slabW = spec.actualWidth + padX;
  const slabH = spec.actualHeight + padY * 2;
  const slabT = 0.038;
  const brickFront = spec.brickDepth * 0.5;
  const slabFront = brickFront - 0.012;
  const slab = new THREE.Mesh(
    new THREE.BoxGeometry(slabW, slabH, slabT),
    new THREE.MeshStandardMaterial({
      color: 0x8a8176,
      roughness: 0.96,
      metalness: 0.0,
      alphaTest: 0.45,
      dithering: false,
    }),
  );
  slab.name = 'damage-mortar';
  slab.position.set(spec.originX + spec.actualWidth * 0.5 + padX * 0.15, spec.actualHeight * 0.5, slabFront - slabT * 0.5);
  slab.castShadow = false;
  slab.receiveShadow = false;

  const mortarCanvas = document.createElement('canvas');
  mortarCanvas.width = 1024;
  mortarCanvas.height = 640;
  const mortarTex = new THREE.CanvasTexture(mortarCanvas);
  mortarTex.colorSpace = THREE.SRGBColorSpace;
  mortarTex.magFilter = THREE.LinearFilter;
  mortarTex.minFilter = THREE.LinearFilter;
  mortarTex.generateMipmaps = false;
  mortarTex.anisotropy = 1;
  mortarTex.wrapS = THREE.ClampToEdgeWrapping;
  mortarTex.wrapT = THREE.ClampToEdgeWrapping;
  (slab.material as THREE.MeshStandardMaterial).map = mortarTex;
  (slab.material as THREE.MeshStandardMaterial).alphaMap = mortarTex;

  const residueData = new Uint8Array(RESIDUE_MAP_W * RESIDUE_MAP_H * 4);
  const residueTex = new THREE.DataTexture(residueData, RESIDUE_MAP_W, RESIDUE_MAP_H, THREE.RGBAFormat);
  residueTex.colorSpace = THREE.NoColorSpace;
  residueTex.magFilter = THREE.LinearFilter;
  residueTex.minFilter = THREE.LinearFilter;
  residueTex.generateMipmaps = false;
  residueTex.wrapS = THREE.ClampToEdgeWrapping;
  residueTex.wrapT = THREE.ClampToEdgeWrapping;
  residueTex.flipY = false;
  residueTex.needsUpdate = true;

  const slabLeft = slab.position.x - slabW * 0.5;
  const slabBottom = slab.position.y - slabH * 0.5;
  handles.setResidue(residueTex, slabLeft, slabBottom, slabW, slabH);

  // Back-only mortar, inset just inside the brick backs. BackSide (no Y-flip)
  // keeps UVs aligned with the shared mortar map and is culled from the front
  // camera, so front stills stay pixel-identical. The old hole-back quad sat
  // *behind* the brick backs with rotation.y = π + FrontSide + albedo 0x140e0c,
  // so the back orbit saw a near-black slab instead of the bricks.
  const backMat = new THREE.MeshStandardMaterial({
    color: 0x8a8176,
    roughness: 0.96,
    metalness: 0.0,
    alphaTest: 0.45,
    dithering: false,
    map: mortarTex,
    alphaMap: mortarTex,
    side: THREE.BackSide,
  });
  const back = new THREE.Mesh(new THREE.PlaneGeometry(slabW + 0.04, slabH + 0.04), backMat);
  back.name = 'damage-back-mortar';
  back.position.set(slab.position.x, slab.position.y, -brickFront + 0.012);
  back.castShadow = false;
  back.receiveShadow = false;

  const hit = new THREE.Mesh(
    new THREE.PlaneGeometry(slabW, slabH),
    new THREE.MeshBasicMaterial({ visible: false, side: THREE.DoubleSide }),
  );
  hit.name = 'damage-hit';
  hit.position.set(slab.position.x, slab.position.y, brickFront + 0.001);

  const ground = new THREE.Mesh(
    new THREE.PlaneGeometry(18, 14),
    new THREE.MeshStandardMaterial({
      color: 0x1a1816,
      roughness: 0.94,
      metalness: 0.02,
    }),
  );
  ground.rotation.x = -Math.PI / 2;
  ground.position.y = 0;
  ground.name = 'damage-ground';
  ground.receiveShadow = false;

  const debrisHandles = createDebrisMaterial();
  const debrisGeom = new THREE.BoxGeometry(1, 1, 1);
  const debrisAttr = new THREE.InstancedBufferAttribute(new Float32Array(DEBRIS_CAP * 4), 4);
  debrisGeom.setAttribute('instanceA', debrisAttr);
  const debrisMesh = new THREE.InstancedMesh(debrisGeom, debrisHandles.material, DEBRIS_CAP);
  debrisMesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
  debrisMesh.frustumCulled = false;
  debrisMesh.castShadow = false;
  debrisMesh.receiveShadow = false;
  debrisMesh.name = 'damage-debris';
  debrisMesh.count = DEBRIS_CAP;
  for (let i = 0; i < DEBRIS_CAP; i += 1) {
    dummy.scale.setScalar(0);
    dummy.position.set(0, -2, 0);
    dummy.updateMatrix();
    debrisMesh.setMatrixAt(i, dummy.matrix);
    color.setRGB(0.5, 0.22, 0.15);
    debrisMesh.setColorAt(i, color);
  }
  if (debrisMesh.instanceColor) debrisMesh.instanceColor.needsUpdate = true;

  group.add(ground);
  group.add(back);
  group.add(slab);
  group.add(mesh);
  group.add(jointMesh);
  group.add(debrisMesh);
  group.add(hit);

  let debugOn = false;

  const wallToUv = (x: number, y: number): { u: number; v: number } => ({
    u: (x - slabLeft) / slabW,
    v: (y - slabBottom) / slabH,
  });

  const paintMortar = (src: DamageLedger, residue: ResidueState): void => {
    const ctx = mortarCanvas.getContext('2d');
    if (!ctx) return;
    const w = mortarCanvas.width;
    const h = mortarCanvas.height;
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.globalCompositeOperation = 'source-over';
    ctx.fillStyle = '#8a8176';
    ctx.fillRect(0, 0, w, h);

    ctx.globalCompositeOperation = 'destination-out';
    ctx.fillStyle = '#000';
    for (let i = 0; i < src.bricks.length; i += 1) {
      if (src.bricks[i].state !== 'removed') continue;
      const p = src.grid.bricks[i];
      const a = wallToUv(p.left - spec.joint * 0.15, p.bottom - spec.joint * 0.15);
      const b = wallToUv(p.right + spec.joint * 0.15, p.top + spec.joint * 0.15);
      ctx.fillRect(a.u * w, (1 - b.v) * h, (b.u - a.u) * w, (b.v - a.v) * h);
    }

    ctx.globalCompositeOperation = 'source-over';
    ctx.strokeStyle = '#1a120e';
    ctx.lineWidth = 10;
    ctx.lineCap = 'butt';
    ctx.lineJoin = 'miter';
    for (let i = 0; i < src.joints.length; i += 1) {
      if (!src.joints[i].cracked) continue;
      const def = src.grid.joints[i];
      const vis = Math.max(residue.crackVis[def.a] ?? 0, residue.crackVis[def.b] ?? 0);
      if (vis < 0.06) continue;
      const a = wallToUv(def.x0, def.y0);
      const b = wallToUv(def.x1, def.y1);
      ctx.globalAlpha = 0.35 + vis * 0.65;
      ctx.beginPath();
      ctx.moveTo(a.u * w, (1 - a.v) * h);
      ctx.lineTo(b.u * w, (1 - b.v) * h);
      ctx.stroke();
    }
    ctx.globalAlpha = 1;

    ctx.strokeStyle = 'rgba(48,45,42,0.88)';
    ctx.lineCap = 'round';
    for (const stain of residue.stains) {
      const a = wallToUv(stain.x, stain.y0);
      const b = wallToUv(stain.x, stain.y1);
      ctx.lineWidth = Math.max(3.5, stain.width * (w / slabW) * 1.6);
      ctx.beginPath();
      ctx.moveTo(a.u * w, (1 - a.v) * h);
      ctx.lineTo(b.u * w, (1 - b.v) * h);
      ctx.stroke();
    }
    mortarTex.needsUpdate = true;
  };

  const paintResidue = (residue: ResidueState, stamps: readonly ResidueStamp[]): void => {
    const baked = bakeResidueMap(residue, stamps, {
      x0: slabLeft,
      y0: slabBottom,
      x1: slabLeft + slabW,
      y1: slabBottom + slabH,
    });
    residueData.set(baked);
    residueTex.needsUpdate = true;
  };

  const syncDebrisMesh = (props: readonly DebrisProp[]): void => {
    for (let i = 0; i < DEBRIS_CAP; i += 1) {
      const p = props[i];
      if (!p) {
        dummy.position.set(0, -4, 0);
        dummy.rotation.set(0, 0, 0);
        dummy.scale.setScalar(0);
        dummy.updateMatrix();
        debrisMesh.setMatrixAt(i, dummy.matrix);
        debrisAttr.setXYZW(i, 0, 0, 0, 0);
        continue;
      }
      dummy.position.set(p.x, p.y, p.z);
      dummy.rotation.set(p.rx, p.ry, p.rz);
      dummy.scale.set(p.hx * 2, p.hy * 2, p.hz * 2);
      dummy.updateMatrix();
      debrisMesh.setMatrixAt(i, dummy.matrix);
      debrisAttr.setXYZW(i, p.seed % 1024, p.soot, p.brokenMask, 0);
      color.setRGB(p.tintR, p.tintG, p.tintB);
      debrisMesh.setColorAt(i, color);
    }
    debrisMesh.instanceMatrix.needsUpdate = true;
    debrisAttr.needsUpdate = true;
    if (debrisMesh.instanceColor) debrisMesh.instanceColor.needsUpdate = true;
  };

  const sync = (
    src: DamageLedger,
    residueArg?: ResidueState,
    stamps: readonly ResidueStamp[] = [],
    debris: readonly DebrisProp[] = [],
  ): void => {
    const residue = residueArg ?? emptyResidue(src.bricks.length);
    for (let i = 0; i < count; i += 1) {
      const brick = src.bricks[i];
      const place = src.grid.bricks[i];
      const removed = brick.state === 'removed';
      dummy.position.set(
        place.x + jitterX[i],
        place.y + jitterY[i],
        place.z - (removed ? 0 : brick.pushIn),
      );
      dummy.rotation.set(rotX[i], rotY[i], rotZ[i]);
      dummy.scale.setScalar(removed ? 0 : 1);
      dummy.updateMatrix();
      mesh.setMatrixAt(i, dummy.matrix);

      instA.setXYZW(i, STATE_CODE[brick.state], hash01(brick.seed, 1), brick.atlasVariant, brick.atlasRot);
      instB.setXYZW(i, brick.cornerMask, brick.edgeDarken, brick.pockCount, brick.throughCrack);
      instC.setXYZW(i, brick.pockU[0], brick.pockV[0], brick.pockU[1], brick.pockV[1]);
      instD.setXYZW(i, brick.pockU[2], brick.pockV[2], brick.throughAngle, brick.throughAngle2);
      instE.setXYZW(i, residue.film[i] ?? 0, residue.scorch[i] ?? 0, residue.crackVis[i] ?? 0, brick.atlasMirror);
      instF.setXYZW(i, brick.backSpall, brick.backScorch, 0, 0);

      if (debugOn) {
        const c = DEBUG_COLORS[brick.state];
        color.setRGB(c[0], c[1], c[2]);
      } else {
        color.setRGB(tints[i][0], tints[i][1], tints[i][2]);
      }
      mesh.setColorAt(i, color);
    }
    mesh.instanceMatrix.needsUpdate = true;
    if (mesh.instanceColor) mesh.instanceColor.needsUpdate = true;
    instA.needsUpdate = true;
    instB.needsUpdate = true;
    instC.needsUpdate = true;
    instD.needsUpdate = true;
    instE.needsUpdate = true;
    instF.needsUpdate = true;

    const jointZ = brickFront - 0.006;
    const jointThick = Math.max(0.007, spec.joint * 0.75);
    for (let i = 0; i < jointCount; i += 1) {
      const rec = src.joints[i];
      const def = src.grid.joints[i];
      const vis = Math.max(residue.crackVis[def.a] ?? 0, residue.crackVis[def.b] ?? 0);
      const show = rec.cracked && vis >= 0.06;
      const mx = (def.x0 + def.x1) * 0.5;
      const my = (def.y0 + def.y1) * 0.5;
      const len = Math.max(0.02, Math.hypot(def.x1 - def.x0, def.y1 - def.y0));
      dummy.position.set(mx, my, show ? jointZ : -10);
      dummy.rotation.set(0, 0, 0);
      if (!show) {
        dummy.scale.setScalar(0);
      } else if (def.kind === 'head') {
        dummy.scale.set(jointThick, len + spec.joint * 0.2, 0.011);
      } else {
        dummy.scale.set(len + spec.joint * 0.2, jointThick, 0.011);
      }
      dummy.updateMatrix();
      jointMesh.setMatrixAt(i, dummy.matrix);
    }
    jointMesh.instanceMatrix.needsUpdate = true;
    paintMortar(src, residue);
    paintResidue(residue, stamps);
    syncDebrisMesh(debris);
  };

  const blitLedger = (canvas: HTMLCanvasElement, src: DamageLedger): void => {
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const w = canvas.width;
    const h = canvas.height;
    ctx.fillStyle = '#0c0d10';
    ctx.fillRect(0, 0, w, h);
    for (let i = 0; i < src.bricks.length; i += 1) {
      const brick = src.bricks[i];
      const p = src.grid.bricks[i];
      const a = wallToUv(p.left, p.bottom);
      const b = wallToUv(p.right, p.top);
      const c = DEBUG_COLORS[brick.state];
      ctx.fillStyle = `rgb(${Math.round(c[0] * 255)},${Math.round(c[1] * 255)},${Math.round(c[2] * 255)})`;
      ctx.fillRect(a.u * w, (1 - b.v) * h, Math.max(1, (b.u - a.u) * w), Math.max(1, (b.v - a.v) * h));
    }
    ctx.strokeStyle = '#1a1410';
    ctx.lineWidth = 1.5;
    for (let i = 0; i < src.joints.length; i += 1) {
      if (!src.joints[i].cracked) continue;
      const j = src.grid.joints[i];
      const a = wallToUv(j.x0, j.y0);
      const b = wallToUv(j.x1, j.y1);
      ctx.beginPath();
      ctx.moveTo(a.u * w, (1 - a.v) * h);
      ctx.lineTo(b.u * w, (1 - b.v) * h);
      ctx.stroke();
    }
  };

  sync(ledger, emptyResidue(count), [], []);

  return {
    group,
    pickables: [hit, mesh, slab],
    sync,
    setDebug(on: boolean) {
      debugOn = on;
      handles.setDebug(on);
    },
    blitLedger,
    dispose() {
      geom.dispose();
      handles.dispose();
      atlas.dispose();
      mortarTex.dispose();
      residueTex.dispose();
      slab.geometry.dispose();
      (slab.material as THREE.Material).dispose();
      jointGeom.dispose();
      jointMat.dispose();
      back.geometry.dispose();
      backMat.dispose();
      hit.geometry.dispose();
      (hit.material as THREE.Material).dispose();
      ground.geometry.dispose();
      (ground.material as THREE.Material).dispose();
      debrisGeom.dispose();
      debrisHandles.dispose();
    },
  };
}
